import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TV_TOKEN_KEY = 'tv_session_token';

const TVAuthContext = createContext(null);

export const useTVAuth = () => {
  const ctx = useContext(TVAuthContext);
  if (!ctx) throw new Error('useTVAuth must be used within TVAuthProvider');
  return ctx;
};

// Axios client that uses TV token (separate from main web auth)
const tvApi = axios.create({ baseURL: API });
tvApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TV_TOKEN_KEY);
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

export { tvApi };

export const TVAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const validate = useCallback(async () => {
    const token = localStorage.getItem(TV_TOKEN_KEY);
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await tvApi.get('/tv/me');
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem(TV_TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { validate(); }, [validate]);

  const setTVToken = (token, userInfo) => {
    localStorage.setItem(TV_TOKEN_KEY, token);
    setUser(userInfo);
  };

  const signout = async () => {
    try {
      await tvApi.post('/tv/signout');
    } catch (e) {
      // Best-effort revocation — local state is cleared regardless
      if (process.env.NODE_ENV === 'development') console.warn('TV signout failed:', e);
    }
    localStorage.removeItem(TV_TOKEN_KEY);
    setUser(null);
  };

  return (
    <TVAuthContext.Provider value={{ user, loading, setTVToken, signout, validate }}>
      {children}
    </TVAuthContext.Provider>
  );
};
